import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const VULN = [
  ["widow", "Widow / widower-headed household"],
  ["elderly", "Elderly-headed household"],
  ["disability", "Disability-related vulnerability"],
  ["vulnerable_children", "Vulnerable children"],
  ["displacement", "Displacement / IDP"],
  ["other", "Other serious vulnerability"],
];

const EMPTY = {
  household_head_name: "", phone: "", email: "", address: "", state: "", lga: "",
  household_size: 1, dependants: 0, monthly_income: 0,
  employment_status: "unemployed", vulnerability_factors: [], reason: "", urgency: "medium",
};

export default function FamilySupport() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { document.title = "Family Food Support — Apply | Dutsen Kauna Foundation"; }, []);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleVuln = (v) => setForm((p) => ({
    ...p,
    vulnerability_factors: p.vulnerability_factors.includes(v)
      ? p.vulnerability_factors.filter((x) => x !== v)
      : [...p.vulnerability_factors, v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, household_size: Number(form.household_size), dependants: Number(form.dependants), monthly_income: Number(form.monthly_income) };
      const { data } = await api.post("/family-support/apply", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div data-testid="family-success" className="bg-[#f8f4e8] py-20 min-h-[70vh]">
        <div className="dk-container max-w-2xl">
          <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
            <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={48} />
            <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">Application received</h1>
            <p className="text-[#0a0d12]/75 mb-6">Thank you. Your application will be assessed by our committee this quarter.</p>
            <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">Application reference</div>
              <div className="font-mono text-3xl md:text-4xl font-bold text-[#d6aa43]" data-testid="family-serial">{result.serial}</div>
              <button onClick={() => navigator.clipboard.writeText(result.serial).then(() => toast.success("Copied"))} className="text-sm text-white hover:text-white inline-flex items-center gap-1 mt-3"><Copy size={14} /> Copy</button>
            </div>
            <div className="text-sm text-[#0a0d12]/70">Quarter: <strong>{result.quarter}</strong> · Preliminary score: <strong>{result.total_score}/100</strong></div>
            <Link to="/empowerment" className="dk-btn-outline mt-6 !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Back to programme</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="family-support-page">
      <section className="relative bg-[#155e3f] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Family Food Support</span>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mt-3 max-w-3xl">Apply for quarterly food assistance.</h1>
          <p className="mt-4 text-white max-w-2xl">Two families receive ₦200,000 each in food support every quarter. Applications are free and evaluated by genuine need — not donation history.</p>
        </div>
      </section>

      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container max-w-3xl">
          <form onSubmit={submit} className="bg-white rounded-2xl p-8 md:p-10 border border-black/5" data-testid="family-form">
            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mb-6">Household details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Household head — full name" required value={form.household_head_name} onChange={(v) => up("household_head_name", v)} testid="fam-name" />
              <F label="Phone" required value={form.phone} onChange={(v) => up("phone", v)} testid="fam-phone" />
              <F label="Email" type="email" required value={form.email} onChange={(v) => up("email", v)} className="sm:col-span-2" testid="fam-email" />
              <F label="Residential address" value={form.address} onChange={(v) => up("address", v)} className="sm:col-span-2" testid="fam-address" />
              <F label="State" value={form.state} onChange={(v) => up("state", v)} testid="fam-state" />
              <F label="Local Government Area" value={form.lga} onChange={(v) => up("lga", v)} testid="fam-lga" />
              <F label="Household size" type="number" value={form.household_size} onChange={(v) => up("household_size", v)} testid="fam-size" />
              <F label="Number of dependants" type="number" value={form.dependants} onChange={(v) => up("dependants", v)} testid="fam-dependants" />
              <F label="Monthly income (₦)" type="number" value={form.monthly_income} onChange={(v) => up("monthly_income", v)} testid="fam-income" />
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Employment status</label>
                <select value={form.employment_status} onChange={(e) => up("employment_status", e.target.value)} className="w-full px-4 py-3 border border-black/15 rounded-md" data-testid="fam-employment">
                  <option value="unemployed">Unemployed</option>
                  <option value="self-employed">Self-employed / informal</option>
                  <option value="employed">Employed</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            </div>

            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mt-8 mb-4">Vulnerability factors</h2>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="vuln-options">
              {VULN.map(([k, l]) => (
                <label key={k} className={`p-3 border-2 rounded-md text-sm cursor-pointer flex items-center gap-2 ${form.vulnerability_factors.includes(k) ? "border-[#d6aa43] bg-[#f8f4e8]" : "border-black/10 hover:border-[#d6aa43]"}`}>
                  <input type="checkbox" checked={form.vulnerability_factors.includes(k)} onChange={() => toggleVuln(k)} data-testid={`vuln-${k}`} />
                  {l}
                </label>
              ))}
            </div>

            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mt-8 mb-4">Urgency</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["low", "Low"], ["medium", "Medium"], ["high", "High"], ["severe", "Severe"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => up("urgency", k)}
                  className={`p-3 rounded-md text-sm font-semibold border-2 transition ${form.urgency === k ? "bg-[#155e3f] text-[#d6aa43] border-[#0d1724]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                  data-testid={`urg-${k}`}>
                  {l}
                </button>
              ))}
            </div>

            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Describe your family's current food situation *</label>
              <textarea required rows={5} value={form.reason} onChange={(e) => up("reason", e.target.value)} className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid="fam-reason" />
            </div>

            <button type="submit" disabled={loading} className="dk-btn-primary mt-6 w-full justify-center disabled:opacity-60" data-testid="fam-submit">
              {loading ? "Submitting…" : "Submit application"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, className = "", testid }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={testid} />
    </div>
  );
}
